import * as React from "react";
import {
    Avatar,
    Badge,
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import Select, { StylesConfig } from "react-select";
import { ContentUkm, UserResponse } from "../../constants/types";

type UkmOption = {
    value: number;
    label: string;
    data: ContentUkm;
};

type Props = {
    user: UserResponse["data"] | undefined;
    ukmData: ContentUkm | undefined;
    isLoadingUkm: boolean;
    isLoadingUser: boolean;
    canSwitchUkm: boolean;
    selectUkm: UkmOption | null;
    optionsUkm: UkmOption[];
    onChangeUkm: (selectedOption: any) => void;
};

export default function UkmSelector({
    user,
    ukmData,
    isLoadingUkm,
    isLoadingUser,
    canSwitchUkm,
    selectUkm,
    optionsUkm,
    onChangeUkm,
}: Props) {
    const customStyles: StylesConfig<any> = {
        control: (base: any, state: any) => ({
            ...base,
            paddingBlock: 5,
            background: "#FFFF",
            borderRadius: 8,
            borderColor: state.isFocused ? ukmData?.warna : "#d4d4d4",
            borderWidth: 1,
            boxShadow: state.isFocused ? null : null,
            cursor: "pointer",
            "&:hover": {
                borderColor: state.isFocused ? ukmData?.warna : "black",
            },
        }),
        option: (styles, { isDisabled, isFocused, isSelected }) => ({
            ...styles,
            backgroundColor: isDisabled
                ? undefined
                : isSelected
                  ? "#f5f5f5"
                  : isFocused
                    ? "#f5f5f5"
                    : undefined,
            color: isDisabled ? "#ccc" : "black",
            cursor: "pointer",
            ":active": {
                ...styles[":active"],
                backgroundColor: !isDisabled
                    ? isSelected
                        ? "#ccc"
                        : "white"
                    : undefined,
            },
        }),
        input: (styles) => ({ ...styles, width: 150 }),
        placeholder: (styles) => ({ ...styles }),
        singleValue: (styles) => ({ ...styles, borderBlockEndWidth: 0 }),
    };

    const isProfileIncomplete =
        ukmData?.alamatToko === "-" ||
        !ukmData?.jenisUsaha ||
        !ukmData?.alamatToko;

    return (
        <Box
            width="100%"
            display="flex"
            justifyContent="center"
            marginBottom={2}
        >
            <Stack
                direction="column"
                spacing={1}
                alignItems="center"
                width="100%"
            >
                <Box width="100%">
                    <Box
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        marginBottom={2}
                        sx={{ mt: 4 }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Badge
                                overlap="circular"
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                            >
                                {isProfileIncomplete ? (
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                        }}
                                        variant="dot"
                                        sx={{
                                            "& .MuiBadge-badge": {
                                                backgroundColor: "#fa4c46",
                                                color: "#fa4c46",
                                                boxShadow: `0 0 0 2px #fff`,
                                                "&::after": {
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    borderRadius: "50%",
                                                    animation:
                                                        "ripple 1.2s infinite ease-in-out",
                                                    border: "1px solid currentColor",
                                                    content: '""',
                                                },
                                            },
                                            "@keyframes ripple": {
                                                "0%": {
                                                    transform: "scale(.8)",
                                                    opacity: 1,
                                                },
                                                "100%": {
                                                    transform: "scale(2.4)",
                                                    opacity: 0,
                                                },
                                            },
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                backgroundColor: "primary.main",
                                                width: 50,
                                                height: 50,
                                            }}
                                            alt={user?.namaLengkap}
                                            src={
                                                user?.gambar ??
                                                user?.namaLengkap
                                            }
                                        />
                                    </Badge>
                                ) : (
                                    <Avatar
                                        sx={{
                                            backgroundColor: "primary.main",
                                            width: 50,
                                            height: 50,
                                        }}
                                        alt={user?.namaLengkap}
                                        src={user?.gambar ?? user?.namaLengkap}
                                    />
                                )}
                            </Badge>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                    textAlign="left"
                                >
                                    {user?.namaLengkap}
                                </Typography>
                                <Stack
                                    spacing={1}
                                    direction="row"
                                    alignItems="center"
                                >
                                    <Typography
                                        textAlign="center"
                                        variant="caption"
                                        component="div"
                                        sx={{
                                            width: "75px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {isLoadingUkm || isLoadingUser ? (
                                            <CircularProgress
                                                size={20}
                                                sx={{ mt: 2 }}
                                            />
                                        ) : (
                                            ukmData?.namaToko
                                        )}
                                    </Typography>
                                    {isLoadingUkm || isLoadingUser ? null : (
                                        <Chip
                                            label={
                                                ukmData?.isPro === 1
                                                    ? "Standar"
                                                    : "FREE"
                                            }
                                            size="small"
                                            sx={{
                                                backgroundColor: "#45A779",
                                                color: "#fff",
                                                fontSize: "12px",
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                            <IconButton
                                color="inherit"
                                aria-label="open menu"
                                edge="start"
                                component={Link}
                                to="/pengaturan"
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                    {canSwitchUkm && (
                        <Box sx={{ width: "100%", padding: "20px" }}>
                            <Select
                                styles={customStyles}
                                value={selectUkm}
                                options={optionsUkm}
                                onChange={onChangeUkm}
                                isLoading={isLoadingUkm}
                                isDisabled={
                                    user?.countProUkm === 0 || isLoadingUkm
                                }
                                components={{
                                    IndicatorSeparator: () => null,
                                }}
                                menuPlacement="auto"
                                placeholder="Pilih UKM..."
                                noOptionsMessage={() =>
                                    "Tidak ada UKM tersedia"
                                }
                            />
                        </Box>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}
